(ns practitest.frontend.components2.dashboard.chart
  (:require [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components2.dashboard.card :refer [card]]
            [practitest.frontend.components2.dashboard.charts.protocol :as p]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            [practitest.frontend.components2.dashboard.charts.bar]
            [practitest.frontend.components2.dashboard.charts.gauge]
            [practitest.frontend.components2.dashboard.charts.readiness-gauge]
            [practitest.frontend.components2.dashboard.charts.pie]
            [practitest.frontend.components2.dashboard.charts.line]
            [practitest.frontend.components2.dashboard.charts.stacked-bar]
            [practitest.frontend.components2.dashboard.charts.age]
            [practitest.frontend.components2.dashboard.charts.stacked-progress-bar]
            [practitest.frontend.components2.dashboard.charts.table-chart :as table-chart]
            [practitest.frontend.components2.tooltip :as t]
            [practitest.frontend.components2.dashboard.subs :as subs]
            [practitest.frontend.components2.dashboard.charts.render]
            [practitest.frontend.events :as events]
            [practitest.frontend.components2.dashboard.charts.vega]
            [re-frame.core :as rf]
            [reagent.core :as reagent]
            ["react" :refer [useEffect useRef]]))

(defmethod p/svg-for-export :default [chart svg-element]
  (let [split? (p/split-layout? chart)
        source (if split? (.closest svg-element ".chart-frame") svg-element)
        clone  (if split?
                 (rh/clone-split-svg source)
                 (rh/clone-svg svg-element))]
    (rh/embed-styles! source clone
                      [".legend-label" ".label-content" ".y-axis .tick line"]
                      {:extra-css (str ".legend-label { font-size: 8px !important; }\n"
                                       ".label-content { font-size: 8px !important; }\n"
                                       "text { font-size: 8px; }\n")})
    (rh/pad-viewbox! clone 30)
    clone))

(defn- tooltip
  "Tooltip driven by a Reagent atom. D3 writes {:text ... :element ...} or nil."
  [state]
  (let [{:keys [text element]} @state]
    [t/tooltip
     {:show?  (some? element)
      :target element}
     (fn [_] [:span])
     text]))

(defn- menu-items [title svg-ref]
  (let [filename (or title "chart")
        items    [[:png "Download as PNG"]
                  [:jpeg "Download as JPEG"]
                  [:svg "Download as SVG"]]]
    (into [{:key  :print
            :text "Print"
            :evt  [::events/print-svg
                   {:svg-element svg-ref
                    :title       title}]}]
          (for [[format text] items]
            {:key  format
             :text text
             :evt  [::events/export-svg
                    {:format      format
                     :svg-element svg-ref
                     :filename    (str filename "." (name format))}]}))))

(defn- warning-view [{:keys [type message]} link]
  [:div.chart-error {:class (some-> type name)}
   [:div.main-message
    (when (= type :irrelevant-tab-filter)
      [icons/mux-info-circle])
    message]
   (when (contains? #{:error-configuration :no-data} type)
     [:div.additional-message
      [:span (get-translation "dashboards.chart.warning-part1" "To resolve this, go to ") ]
      [:a {:href link} (get-translation "dashboards.chart.settings" "Settings")]
      [:span (str (get-translation "dashboards.chart.warning-part2" " and update the configuration") ".")]]
     )])

(defn ref-view
  "Wrap a React ref so that @deref applies f to .-current."
  [ref f]
  (reify IDeref
    (-deref [_] (f (.-current ref)))))

(defn use-effect [f deps]
  "useEffect wrapper, compare cljs data"
  (let [ref (useRef deps)]
    (when-not (= deps (.-current ref))
      (set! (.-current ref) deps))
    (useEffect f #js [(.-current ref)])))

(defn svg
  ([item] (svg item nil))
  ([{:chart/keys [type] :as item} ext-svg-ref]
   (let [svg-ref         (or ext-svg-ref (useRef nil))
         frame-ref       (useRef nil)
         y-axis-ref      (useRef nil)
         legend-ref      (useRef nil)
         overlay-ref     (useRef nil)
         split?          (p/split-layout? item)
         tooltip-state   (reagent/atom nil)
         toggle-tooltip! (fn [tooltip]
                           (if (= @tooltip-state tooltip)
                             (reset! tooltip-state nil)
                             (reset! tooltip-state tooltip)))
         config-common   {:dispatch-fn     #(rf/dispatch [%1 (:chart/id item) %2])
                          :tooltip         tooltip-state
                          :toggle-tooltip! toggle-tooltip!}]
     ;; (useEffect f #js [(hash data)]) does not work
     ;; https://clojure.atlassian.net/browse/CLJS-3410
     (use-effect
      (fn []
        (let [svg-el   (.-current svg-ref)
              ctx      (cond-> config-common
                         split? (assoc :split-targets {:y-axis  (.-current y-axis-ref)
                                                       :legend  (.-current legend-ref)
                                                       :overlay (.-current overlay-ref)}))
              observer (js/ResizeObserver. (fn []
                                             (rh/update-svg-scale! svg-el)
                                             (p/render! item ctx svg-el)))]
          (.observe observer (if split? (.-current frame-ref) svg-el))
          #(.disconnect observer)))
      item)
     (let [chart-svg [:svg.chart {:class               (when-not split? (some-> type name))
                                  :ref                 svg-ref
                                  :preserveAspectRatio "xMidYMid meet"}]]
       [:<>
        (if split?
          [:div.chart-frame {:class (some-> type name)
                             :ref   frame-ref}
           [:svg.chart-y-axis {:ref y-axis-ref}]
           [:div.chart-scroll
            chart-svg]
           [:svg.chart-legend {:ref legend-ref}]
           [:svg.chart-overlay {:ref overlay-ref}]]
          chart-svg)
        [tooltip tooltip-state]]))))

(defn external-view? []
  (or (boolean (aget js/window "_pt" "is_iframe"))
      (boolean (aget js/window "_pt" "is_external"))))

(defn- chart-inner
  [{:chart/keys [title sub-title context warning] :as item}
   {:chart/keys [edit-url edit-disabled-tooltip]}]
  (let [svg-ref        (useRef nil)
        export-svg-ref (ref-view svg-ref #(p/svg-for-export item %))
        on-edit        (when-not (external-view?) #(rf/dispatch [::events/navigate edit-url]))
        hide-chart?    (and warning (not= :irrelevant-tab-filter (:type warning)))]
    [card
     {:title                 title
      :sub-title             sub-title
      :context               context
      :on-edit               on-edit
      :edit-disabled-tooltip edit-disabled-tooltip
      :dropdown-menu         (menu-items title export-svg-ref)}
        [:div.chart-wrapper
         (when-not hide-chart? [:f> svg item svg-ref])
         (when warning [warning-view warning edit-url])]]))

(defn- table-chart-inner
  [{:chart/keys [title sub-title context warning cell-height] :as item}
   {:chart/keys [edit-url edit-disabled-tooltip]}]
  (let [on-edit     (when-not (external-view?) #(rf/dispatch [::events/navigate edit-url]))
        hide-chart? (and warning (not= :irrelevant-tab-filter (:type warning)))
        double-height? (= cell-height :double)]
    [card
     {:title                 title
      :sub-title             sub-title
      :context               context
      :on-edit               on-edit
      :edit-disabled-tooltip edit-disabled-tooltip
      :class                 (when double-height? "double-height")}
        [:div.chart-wrapper
         (when-not hide-chart? [table-chart/view item])
         (when warning [warning-view warning edit-url])]]))

(defn chart
  "Generic chart component. Subscribes to chart data and delegates rendering."
  [{:chart/keys [id] :as props}]
  (when-let [item @(rf/subscribe [::subs/chart id])]
    (if (= (:chart/type item) ::table)
      [:f> table-chart-inner item props]
      [:f> chart-inner item props])))
